// src/components/sourcex/OptionsEditor.jsx
//
// Editor for the answer choices of a dropdown or checklist.
//
// Each choice is its own row that can be renamed, reordered or deleted, and a
// choice that controls a branch says so. No cap on how many choices can be
// added — see templateSchema.js's makeQuestion/makeColumn.
//
// Extracted from SectionEditor.js so both the question builder and a table
// column editor can reuse the exact same choice-editing UI.

import { useState } from "react";
import { Plus, ChevronUp, ChevronDown, X, Eye } from "lucide-react";
import { inputCls } from "./QuestionField";
import { PREFILL_HINT, PREFILL_LABEL, prefillChipCls } from "./DefaultValueInput";

/**
 * Split a pasted list into choices: one per line, or comma-separated, blanks
 * dropped. Exported because whoever OWNS the save button has to be able to
 * read a box this component is still holding — see `onPendingChange`.
 */
export const parseOptionList = (text) =>
  String(text || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * @param {"Select"|"MultiSelect"} type
 * @param {string[]} options
 * @param {(next: string[]) => void} onChange
 * @param {(option: string) => Array} [usedBy]  fields whose rule needs this choice
 * @param {Function} [confirm]  the shared confirm-dialog hook — deleting a
 *   choice another field branches on asks first
 * @param {string|string[]} [defaultValue]  which choice(s) print already marked
 * @param {(next: string|string[]) => void} [onDefaultChange]  supply this and the
 *   mark beside each choice becomes clickable — that IS the "starts as" control.
 *   A separate box asking the same question again is what this replaces, so it
 *   is opt-in: a table COLUMN's starting values are per-cell (prefillRows, set
 *   on the grid), and there is no single default to offer there.
 * @param {(text: string) => void} [onPendingChange]  told, on every keystroke,
 *   what is sitting UNCOMMITTED in the "Paste a list" box.
 *
 *   A parent that owns a Save button supplies this and folds the staged text
 *   into what it saves (see parseOptionList), which is what makes one click on
 *   Save both keep the pasted list and close the editor. Committing on blur
 *   instead cannot: blur fires between mousedown and mouseup, the new choice
 *   rows push Save down out from under the pointer, and the click never lands —
 *   so saving took two clicks and the first one looked like it did nothing.
 *
 *   Without it, the box falls back to committing on blur, which is right where
 *   there is no Save button underneath to displace (the table-column popover).
 */
export default function OptionsEditor({
  type,
  options,
  onChange,
  usedBy,
  confirm,
  defaultValue,
  onDefaultChange,
  onPendingChange,
}) {
  const isChecklist = type === "MultiSelect";
  const list = Array.isArray(options) ? options : [];
  const [bulk, setBulk] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  // Starting values, with exactly the semantics the printed form has: a
  // dropdown starts on ONE choice, a checklist can start with several ticked.
  const canPickDefault = typeof onDefaultChange === "function";
  const chosen = isChecklist
    ? (Array.isArray(defaultValue) ? defaultValue : []).map(String)
    : [];
  const isDefault = (opt) =>
    isChecklist ? chosen.includes(String(opt)) : defaultValue === opt;
  const toggleDefault = (opt) => {
    if (isChecklist) {
      onDefaultChange(
        isDefault(opt)
          ? chosen.filter((o) => o !== String(opt))
          : [...chosen, String(opt)]
      );
    } else {
      // Clicking the marked one again clears it — that is how you get back to
      // "starts blank" without a separate control to do it.
      onDefaultChange(isDefault(opt) ? "" : opt);
    }
  };
  // The same marks the form prints, so the editor and the paper agree.
  const glyph = (on) =>
    isChecklist ? (on ? "☑" : "☐") : on ? "●" : "○";

  const setAt = (i, v) => onChange(list.map((o, oi) => (oi === i ? v : o)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = async (i) => {
    const dependents = usedBy ? usedBy(list[i]) : [];
    if (dependents.length) {
      const names = dependents.map((d) => `• ${d.question}`).join("\n");
      const ok = confirm
        ? await confirm(
            `“${list[i]}” is what makes ${dependents.length} other field${
              dependents.length === 1 ? "" : "s"
            } appear:\n\n${names}\n\nThose fields will stop appearing until you point them at a different answer.`,
            {
              title: "Delete this choice?",
              confirmLabel: "Delete anyway",
              tone: "danger",
            }
          )
        : true;
      if (!ok) return;
    }
    onChange(list.filter((_, oi) => oi !== i));
  };
  const add = () => onChange([...list, ""]);

  // Commit whatever is staged in the paste box into the list. No-op on an
  // empty box.
  const flushBulk = () => {
    const added = parseOptionList(bulk);
    if (!added.length) return;
    onChange([...list, ...added]);
    setBulk("");
    onPendingChange?.("");
  };

  const addBulk = () => {
    flushBulk();
    setShowBulk(false);
  };

  // Every keystroke is reported to a parent that asked for it, so the staged
  // text is never this component's secret. See onPendingChange.
  const editBulk = (value) => {
    setBulk(value);
    onPendingChange?.(value);
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-100">
          {isChecklist ? "Checklist boxes" : "Dropdown choices"}
        </span>
        <span className="text-[11px] text-slate-500">
          {isChecklist ? "tick more than one" : "pick exactly one"}
        </span>
        <span className="ml-auto text-[11px] tabular-nums text-slate-500">
          {list.length}
        </span>
      </div>

      {list.length === 0 && (
        <p className="text-[11px] text-amber-200/90">
          Add at least one choice.
        </p>
      )}

      {/* The pre-fill control, announced. A choice type sets its pre-fill by
          clicking the mark beside a choice — there is no separate box — so
          without this the feature was a click nobody knew was there. Same chip,
          same name and same blue as the box a write-in answer gets in
          QuestionBuilder, because it IS that feature: one idea, one look.
          Sits directly above the marks it is talking about. */}
      {canPickDefault && list.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <span title={PREFILL_HINT} className={prefillChipCls} data-tour="builder-prefill">
            {PREFILL_LABEL}
          </span>
          <span className="min-w-0 text-[11px] text-blue-200/80">
            Click a {isChecklist ? "☐" : "○"} to print that one already marked.
          </span>
        </div>
      )}

      <div className="space-y-1">
        {list.map((opt, i) => {
          const dependents = usedBy && opt ? usedBy(opt) : [];
          return (
            <div key={i} className="space-y-0.5"> {/* eslint-disable-line react/no-array-index-key */}
              <div className="flex items-center gap-1">
                {/* Big enough to READ. At text-xs a ● and an ○ (or a ☑ and a
                    ☐) differ by a few pixels of ink, so "is this one already
                    marked?" could not be answered at a glance — which is the
                    only question this control exists to answer. */}
                {canPickDefault ? (
                  <button
                    type="button"
                    onClick={() => toggleDefault(opt)}
                    aria-pressed={isDefault(opt)}
                    title={
                      isDefault(opt)
                        ? "Prints already marked — click to clear"
                        : "Print this one already marked"
                    }
                    // Blue when marked — the same colour the write-in types'
                    // pre-fill box wears in QuestionBuilder, because this IS
                    // that feature for a choice type. One colour for one idea.
                    className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-lg leading-none transition ${
                      isDefault(opt)
                        ? "text-blue-100 bg-blue-500/25 ring-1 ring-blue-400/50 hover:bg-blue-500/35"
                        : "text-slate-500 hover:text-slate-100 hover:bg-white/10"
                    }`}
                  >
                    {glyph(isDefault(opt))}
                  </button>
                ) : (
                  <span className="shrink-0 w-8 text-center text-lg leading-none text-slate-500">
                    {glyph(false)}
                  </span>
                )}
                <input
                  className={`${inputCls} flex-1 min-w-0 !py-1.5 text-xs`}
                  value={opt}
                  onChange={(e) => setAt(i, e.target.value)}
                  placeholder={`Choice ${i + 1}`}
                />
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  title="Move this choice up"
                  className="shrink-0 w-6 h-7 rounded flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  title="Move this choice down"
                  className="shrink-0 w-6 h-7 rounded flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  title="Delete this choice"
                  className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/15 transition"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {dependents.length > 0 && (
                <p className="pl-5 text-[10px] text-sky-200/80 flex items-center gap-1">
                  <Eye className="w-3 h-3 shrink-0" />
                  Shows: {dependents.map((d) => d.question).join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 transition"
        >
          <Plus className="w-3 h-3" /> Add {isChecklist ? "a box" : "a choice"}
        </button>
        <button
          type="button"
          onClick={() => setShowBulk((v) => !v)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-slate-300 bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] transition"
        >
          Paste a list
        </button>
      </div>

      {showBulk && (
        <div className="space-y-1">
          <textarea
            className={`${inputCls} h-20 text-xs`}
            value={bulk}
            onChange={(e) => editBulk(e.target.value)}
            // Only when nobody upstream is watching the staged text — with
            // onPendingChange the save path already has it, and committing here
            // would move the Save button out from under the click.
            onBlur={onPendingChange ? undefined : flushBulk}
            placeholder={"One per line (or separated by commas)\nMild\nModerate\nSevere"}
          />
          <button
            type="button"
            onClick={addBulk}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 transition"
          >
            <Plus className="w-3 h-3" /> Add these to the list
          </button>
          {/* Says the box is not a trap either way it is wired. */}
          <p className="text-[10px] text-slate-500">
            {onPendingChange
              ? "Saving the question keeps whatever is in this box, too."
              : "Added automatically when you click away, too."}
          </p>
        </div>
      )}

      {/* The pre-fill line that used to be tacked on here now sits ABOVE the
          choices, next to the marks it describes — see the chip. */}
      <p className="text-[10px] text-slate-500">
        Renaming a choice updates any rule that uses it.
      </p>
    </div>
  );
}
